// SELF — the faculties currently in play. Progressive reveal (handoff §8):
// only the tier the user is working on is foregrounded; secured lower tiers
// become quiet structure, higher tiers stay veiled.
import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { normalizeMaladyId } from '../olmps/spectrum-tools';
import { ascensionRank, GEAR_TIERS, TIER_ORDER, type GearId, type TierId } from '../olmps/ascension';
import { computeProgress, ladderLabel, todayKey, type ProgressStats } from '../olmps/deity-progress';
import { formatSeat, type SeatScore } from '../olmps/seatScore';
import type { GameSettings } from '../olmps/gameSettings';
import { useSeats } from '../olmps/useSeats';
import { deityLevelData } from '../olmps/deity-data';
import { useCompassConfig } from '../olmps/sectorConfig';
import { DMT_TOOLS, type DmtToolId } from '../olmps/spectrum-tools';
import { GEAR_ORDER, SEATS, SEAT_BY_ID, type Seat } from './roster';

export type SeatState = 'active' | 'secured' | 'veiled';

/** Directive §3 acquisition state (lock is the separate `state === 'veiled'` reading). */
export type AcquisitionState = 'unlocked' | 'training' | 'acquired' | 'acquired_unseated';

export interface SeatView {
  seat: Seat;
  stats: ProgressStats;
  state: SeatState;
  /** §5 rolling-30-day seat score — a SEPARATE reading from the ladder in `stats`. */
  seatScore: SeatScore;
  /** §3 state from the ladder + the seat: an acquired deity the malady out-scores is 'acquired_unseated'. */
  acquisition: AcquisitionState;
  /** Ladder reading, e.g. "Training · 1 of 3 Goals" — separate from the seat. */
  ladderLine: string;
  /** Seat reading, e.g. "Seat +2.4 · ATLAS holds" — separate from the ladder. */
  seatLine: string;
  /** The malady holding the seat while the deity isn't secured. */
  maladyName: string;
  /** Malady subtype id (the intake-picked one if present, else the primary) — keys malady art. */
  maladyId: string;
  /** Whoever holds the seat right now — the SIGN of the §5 seat score: 'malady'
   *  only when the score is below 0. Contested (0) and "—" show the deity; the
   *  hall never guesses a malady. Acquisition does not force the deity: an
   *  acquired deity whose seat score is negative is "Acquired · unseated". */
  holder: 'deity' | 'malady';
  /** SEAT standing: −4..+4 seat score (null = "—", under 8 days of marks) and its state word. */
  standing: { position: number | null; label: string };
}

export interface SelfState {
  loading: boolean;
  rank: 'MORTAL' | 'DEMI' | 'TYTN' | 'OLMPN';
  securedCount: number;
  /** The tier currently being worked: the lowest tier not fully secured. */
  workingTier: TierId;
  /** The five foregrounded seats (one per gear, at the working tier). */
  active: SeatView[];
  /** Every seat with its state, for the full Mountain. */
  all: SeatView[];
  byGear: Record<GearId, SeatView>;
}

// Intake context only: the seat never reads displayed_position (seats start at 0).
/** "Seat +2.4 · ATLAS holds" / "Seat −1.2 · Planē holds" / "Seat — · needs 8 days of marks". */
export function seatLineOf(sc: SeatScore | undefined, deityName: string, maladyName: string, game: GameSettings): string {
  if (!sc || sc.score == null) {
    const n = sc?.daysOfMarks ?? 0;
    return `Seat — · ${n > 0 ? `${n} of ${game.seatMinDays} days of marks` : `needs ${game.seatMinDays} days of marks`}`;
  }
  const who = sc.holder === 'malady' ? `${maladyName || 'the malady'} holds` : sc.holder === 'deity' ? `${deityName} holds` : 'contested';
  return `Seat ${formatSeat(sc)} · ${who}`;
}

interface DmtRow {
  tool_id: string;
  malady_subtype: string | null;
}

export function useSelf(userId: string | undefined): SelfState {
  const { config, loading } = useCompassConfig(userId);
  const { seats, loading: seatsLoading } = useSeats(userId);
  const [rows, setRows] = useState<Record<string, DmtRow>>({});
  useEffect(() => {
    if (!userId) return;
    let live = true;
    supabase
      .from('dmt_state')
      .select('tool_id, malady_subtype')
      .eq('user_id', userId)
      .then(({ data }) => {
        if (!live || !data) return;
        setRows(Object.fromEntries((data as DmtRow[]).map((r) => [r.tool_id, r])));
      });
    return () => {
      live = false;
    };
  }, [userId]);

  return useMemo(() => {
    const today = todayKey();
    const statsOf = (id: DmtToolId) =>
      computeProgress(config.principleProgress?.[id], deityLevelData(id), today, id, { ledger: config.ledger, game: config.game });

    const statsMap = new Map<DmtToolId, ProgressStats>();
    for (const t of DMT_TOOLS) statsMap.set(t.id, statsOf(t.id));
    // Acquired deities among the 15 seats only — never the wider DMT_TOOLS list.
    const securedCount = SEATS.filter((s) => statsMap.get(s.id)?.secured).length;

    const workingTier =
      TIER_ORDER.find((tier) => GEAR_ORDER.some((g) => !statsMap.get(GEAR_TIERS[g][tier])?.secured)) ?? 'olympian';
    const workingIdx = TIER_ORDER.indexOf(workingTier);

    const all: SeatView[] = GEAR_ORDER.flatMap((gear) =>
      TIER_ORDER.map((tier, idx) => {
        const id = GEAR_TIERS[gear][tier];
        const stats = statsMap.get(id)!;
        const tool = DMT_TOOLS.find((t) => t.id === id);
        // 'veiled' = not yet scored (cloud on the Mountain). It is a visual state
        // only — every tool is usable from day one (ruling 1.2).
        const state: SeatState = idx === workingIdx ? 'active' : idx < workingIdx || stats.secured ? 'secured' : 'veiled';
        const row = rows[id];
        const picked = row?.malady_subtype ? normalizeMaladyId(id, row.malady_subtype) : null;
        const mal = tool?.maladies.find((m) => m.id === picked) ?? tool?.maladies[0];
        const sc = seats[id];
        const holder: 'deity' | 'malady' = sc?.holder === 'malady' ? 'malady' : 'deity';
        const acquisition: AcquisitionState = stats.secured
          ? holder === 'malady' ? 'acquired_unseated' : 'acquired'
          : stats.rung === 'training' ? 'training' : 'unlocked';
        const word = { acquired: 'Acquired', acquired_unseated: 'Acquired · unseated', training: 'Training', unlocked: 'Unlocked' }[acquisition];
        return {
          seat: SEAT_BY_ID[id],
          stats,
          state,
          seatScore: sc,
          acquisition,
          ladderLine: ladderLabel(stats, config.game),
          seatLine: seatLineOf(sc, SEAT_BY_ID[id].name, mal?.name ?? '', config.game),
          maladyName: mal?.name ?? '',
          maladyId: mal?.id ?? '',
          holder,
          standing: { position: sc?.score ?? null, label: word },
        } as SeatView;
      }),
    );
    const active = all.filter((v) => v.state === 'active');
    const byGear = Object.fromEntries(active.map((v) => [v.seat.gear, v])) as Record<GearId, SeatView>;

    return { loading: loading || seatsLoading, rank: ascensionRank(securedCount), securedCount, workingTier, active, all, byGear };
  }, [config, loading, seatsLoading, rows, seats]);
}
